import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import moment from 'moment-timezone';

import { I18nService } from '@recruitee/i18n';

import { DropdownWidth } from '../../../common/dropdown/dropdown.component';
import { KeyCode } from '../../../core/core.module';
import { RtStatefulComponent } from '../../../utils/rt-stateful-component';

type State = {
  timesArr: string[];
  selectionIndex: number | null;
};

@Component({
  selector: 'rt-time-picker-dropdown',
  templateUrl: './time-picker-dropdown.component.html',
  styleUrls: ['./time-picker-dropdown.component.less'],
  host: {
    '(window:keydown)': 'onKeyDown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimePickerDropdownComponent extends RtStatefulComponent<State> {
  @Input() public currentSelected: string;
  @Input() public duration: boolean = false;
  @Input() public durationInterval: number = 5;
  @Input() public durationMin: number = 0;
  @Input() public durationMax: number = 300;
  @Input() public timeInterval: number = 0;

  @Output() public timeSelect: EventEmitter<string> = new EventEmitter();

  @ViewChild('timeDropdownContainer', { read: ElementRef, static: true })
  public timeDropdownContainerRef: ElementRef;
  @ViewChildren('timeItemsQuery', { read: ElementRef })
  public itemsQueryList: QueryList<ElementRef>;
  public DropdownWidth = DropdownWidth;

  private currentItem: HTMLElement;

  constructor(private i18n: I18nService, protected cdRef: ChangeDetectorRef) {
    super(cdRef);
    this.createState({ timesArr: [], selectionIndex: null });
  }

  public ngOnInit(): void {
    if (this.duration) {
      this.setState({ ...this.state, timesArr: this.prepareDurationData() });
    } else {
      this.prepareTimeArray();
    }
    this.setState({ ...this.state, selectionIndex: this.getSelectionIndex() });
  }

  public ngAfterViewInit(): void {
    this.setScrollOffset();
  }
  public trackBy(index: number, item: string): string {
    return item;
  }

  public onTimeSelect(index: number): void {
    this.timeSelect.emit(this.state.timesArr[index]);
  }

  public onKeyDown($event: KeyboardEvent): void {
    if ($event.keyCode === KeyCode.DownArrow) {
      if (this.state.selectionIndex! < this.state.timesArr.length - 1) {
        this.setState({ ...this.state, selectionIndex: (this.state.selectionIndex! += 1) });
      }
    }
    if ($event.keyCode === KeyCode.UpArrow) {
      if (this.state.selectionIndex! > 0) {
        this.setState({ ...this.state, selectionIndex: (this.state.selectionIndex! -= 1) });
      }
    }
    if ($event.keyCode === KeyCode.Enter) {
      this.currentItem.click();
    }
    if ([KeyCode.Enter, KeyCode.DownArrow, KeyCode.UpArrow].includes($event.keyCode)) {
      this.setScrollOffset();
    }
  }

  private setScrollOffset(): void {
    const timeContainer = this.timeDropdownContainerRef.nativeElement;
    const timeItemRef = this.itemsQueryList.find(
      (item, index) => index === this.state.selectionIndex,
    );

    if (!timeContainer || !timeItemRef) {
      return;
    }

    const timeItem = timeItemRef.nativeElement;
    this.currentItem = timeItem;

    const itemRelativeTop = timeItem.offsetTop - timeContainer.offsetTop;
    timeContainer.scrollTop = itemRelativeTop;
  }

  private getCurrentHour(): number {
    const date = new Date();
    return date.getHours();
  }

  private prepareDurationData(): string[] {
    const data = [];
    const mins = ' ' + this.i18n.translate('rtu.interview.schedule_dialog.minutes');

    for (let i = this.durationMin; i <= this.durationMax; i += this.durationInterval) {
      data.push(i.toString() + mins);
    }

    return data;
  }

  private prepareTimeArray(): void {
    const minutesSteps = parseInt(`${60 / this.timeInterval}`, 10) || 1;

    const timesArr = [];
    for (let i = 0; i <= 23; i++) {
      for (let m = 0; m <= minutesSteps - 1; m++) {
        const time = moment(`${i}:${m * this.timeInterval}`, 'H:mm').format('LT');
        timesArr.push(time);
      }
    }
    this.setState({ ...this.state, timesArr });
  }

  private getSelectionIndex(): number {
    if (!this.currentSelected && !this.duration) {
      const currentHour = this.getCurrentHour();
      return currentHour * 4;
    } else if (!this.currentSelected && this.duration) {
      return 0;
    } else if (this.duration) {
      const currentSelectedTime = parseInt(this.currentSelected, 10);
      return (
        <number>(
          this.state.timesArr.reduce(
            (result, time, index) => (parseInt(time, 10) <= currentSelectedTime ? index : result),
            null,
          )
        ) || 0
      );
    } else {
      return this.state.timesArr.findIndex(time => {
        const currentTime = moment(this.currentSelected, 'LT');
        const timeToSelect = moment(time, 'LT');

        return currentTime.isSameOrBefore(timeToSelect);
      });
    }
  }
}
